import { Button } from "@/components/ui/button";
import usePlaceOrder from "@/hooks/usePlaceOrder";
import { formatAmount } from "@/lib/utils";
import { emailRegex } from "@/lib/validationRules";
import {
  useGetDeliveryRouteDetailsQuery,
  useGetDeliveryRouteListQuery,
} from "@/redux/apiSlices/deliveryApi";
import { usePathname, useRouter } from "next/navigation";
import Script from "next/script";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Loader from "../Loader";
import OrderSummarySkeleton from "../Skeletons/OrderSummarySkeleton";
import ApplyCoupan from "./ApplyCoupan";
import { Checkbox } from "../ui/checkbox";
import Link from "next/link";
import useCartDetails from "@/hooks/useCartDetails";

const OrderSummary = () => {
  const pathName = usePathname();
  const router = useRouter();

  const [isAgree, setIsAgree] = useState(false);

  const isLogin = useSelector((state) => state.auth.isLogin);
  const { id: cartId, cartItems = [] } = useSelector((state) => state.userCart);
  const isCheckout = pathName === "/checkout";
  const cardData = useSelector((state) => state.authorizeDotNetCardDetails);
  const userDefaultZipcode =
    useSelector((state) => state?.userLocation?.zipCode) || "94545";
  const { isAllProductsInActive } = useCartDetails();

  const {
    email,
    address,
    paymentMethod,
    isAddressEditable,
    isDeliveryProductsEditable,
    isPickUpProductsEditable,
    isCustomBillingAddressValid,
    useAsBillingAddress,
    billingAddressId,
  } = useSelector((state) => state.orderDetails || {});

  const { zipCode: guestShippingZipcode } = address || {};

  const {
    data: routeDetails,
    isFetching,
    refetch,
  } = useGetDeliveryRouteDetailsQuery(
    {
      zipCode: guestShippingZipcode || userDefaultZipcode,
    },
    { skip: cartItems?.length === 0 }
  );

  const { refetch: refetchDeliveryList } = useGetDeliveryRouteListQuery(
    {
      zipCode: guestShippingZipcode || userDefaultZipcode,
    },
    {
      skip: cartItems?.length === 0,
      selectFromResult: ({ data }) => ({ data: data?.data }),
    }
  );

  useEffect(() => {
    if (cartItems?.length > 0) {
      refetch();
      refetchDeliveryList();
    }
  }, [cartItems, refetch]);

  const { total } = cartItems?.length === 0 ? {} : routeDetails || {};

  const { placeOrder, isOrderLoading } = usePlaceOrder(total);

  const isLoading = Boolean(isFetching || isOrderLoading);

  const totalSaving = total?.savings + total?.discount || 0;

  const onButtonClick = () => {
    if (isCheckout) {
      placeOrder();
    } else {
      router.push("/checkout");
    }
  };

  const isButtonDisabled = () => {
    if (isAllProductsInActive) return true;
    if (total?.grandTotal < 5) return true;
    if (!isCheckout && cartItems?.length === 0) return true;
    if (
      isCheckout &&
      (!isAgree ||
        isAddressEditable ||
        isDeliveryProductsEditable ||
        isPickUpProductsEditable)
    )
      return true;
    if (
      isCheckout &&
      (!isLogin
        ? useAsBillingAddress
          ? false
          : !isCustomBillingAddressValid
        : useAsBillingAddress
        ? false
        : !billingAddressId)
    )
      return true;
    if (isCheckout && cartItems?.length > 0 && !paymentMethod) return true;
    if (isCheckout && !isLogin && !emailRegex.test(email)) return true;
    if (paymentMethod === "authorize" && cardData?.cardNumber?.length < 15)
      return true;
    if (paymentMethod === "authorize" && cardData?.month?.length <= 1)
      return true;
    if (paymentMethod === "authorize" && cardData?.year?.length <= 3)
      return true;
    if (paymentMethod === "authorize" && cardData?.cardCode?.length <= 2)
      return true;
    return false;
  };

  return (
    <>
      {isLoading && <Loader />}
      <div className="lg:px-4 lg:w-full sticky top-[94px]">
        <Script
          src={process.env.NEXT_PUBLIC_AFFIRM_SCRIPT_URL}
          strategy="lazyOnload"
          onLoad={() => {
            window._affirm_config = {
              public_api_key: process.env.NEXT_PUBLIC_AFFIRM_SCRIPT_API_KEY,
              locale: "en_US",
              country_code: "USA",
            };

            if (window.affirm) {
              window.affirm.ui.refresh();
            }
          }}
        />

        <div className="lg:max-w-full flex-1 max-w-[383px] py-[26px] px-[24px] rounded-[13px] bg-white sticky top-[95px] mb-[18px] ">
          <h3 className=" text-blue3 text-[17px] leading-[25px] font-medium text-center pb-[10px] border-b border-gray18">
            Order Summary
          </h3>
          {isFetching && <OrderSummarySkeleton />}

          {!isFetching && (
            <>
              <div className="border-b border-gray18 pb-[5px] pt-[15px]">
                <div className="text-sm pb-[10px] flex justify-between items-center	">
                  <p className=" font-medium">
                    {isCheckout ? "Item Total" : "Original Price"}
                  </p>
                  <p className=" font-semibold">
                    {isCheckout
                      ? formatAmount(total?.itemTotal)
                      : formatAmount(total?.subtotal)}
                  </p>
                </div>
                <div className="text-sm pb-[10px] flex justify-between items-center	">
                  {total?.savings !== 0 && !isCheckout && (
                    <>
                      <p className=" font-medium">Savings</p>
                      <p className=" font-semibold text-green1 ">
                        - {formatAmount(total?.savings)}
                      </p>
                    </>
                  )}
                </div>
                {total?.totalDeliveryFee > 0 && (
                  <div className="text-sm pb-[10px] flex justify-between items-center	">
                    <p className=" font-medium">Delivery Charges</p>
                    <p className=" font-semibold">
                      {formatAmount(total?.totalDeliveryFee)}
                    </p>
                  </div>
                )}

                {total?.tax > 0 && (
                  <div className="text-sm pb-[10px] flex justify-between items-center	">
                    <p className=" font-medium">Tax</p>
                    <p className=" font-semibold">{formatAmount(total?.tax)}</p>
                  </div>
                )}

                {total?.discount > 0 && (
                  <div className="text-sm pb-[10px] flex justify-between items-center	">
                    <p className=" font-medium">Promo Code</p>

                    <p className=" font-semibold text-green1 ">
                      - {formatAmount(total?.discount)}
                    </p>
                  </div>
                )}
              </div>

              {total?.grandTotal > 0 && (
                <div className="text-sm my-4 flex justify-between items-center	">
                  <p className=" font-medium">Total</p>
                  <p className=" font-semibold">
                    {formatAmount(total?.grandTotal)}
                  </p>
                </div>
              )}
              {isCheckout && !!totalSaving && (
                <p className=" my-4 text-sm font-semibold text-green1 ">
                  You&apos;re saving {formatAmount(totalSaving)} on your order
                  today!
                </p>
              )}
            </>
          )}

          <ApplyCoupan total={total} cartId={cartId} />
          {isCheckout && (
            <div className="flex items-start space-x-2 text-[12px] mb-6">
              <Checkbox
                id="terms"
                onCheckedChange={(e) => setIsAgree(e)}
                checked={isAgree}
              />
              <label
                htmlFor="terms"
                className=" text-[12px] text-gray31 leading-[15px] peer-disabled:cursor-not-allowed peer-disabled:opacity-70 "
              >
                By placing your order, you agree to our Airport Home Appliance{" "}
                <Link
                  href="/terms-and-conditions"
                  className="underline inline-block"
                >
                  {" "}
                  Terms & Conditions
                </Link>
                ,{" "}
                <Link
                  href="/privacy-policy-cookie-restriction-mode"
                  className="underline inline-block"
                >
                  Privacy Policy
                </Link>{" "}
                &{" "}
                <Link href="/sms-terms" className="underline inline-block">
                  SMS Terms
                </Link>
              </label>
            </div>
          )}
          <Button
            className="text-[14px] font-semibold text-white bg-red3 h-[40px] rounded-[8px] w-full"
            onClick={onButtonClick}
            disabled={isButtonDisabled() || isLoading}
          >
            {isCheckout ? "Place Order" : " Proceed to Checkout"}
          </Button>
          {/* <p className=" text-[14px] font-medium text-blue3 p-2 text-center">
            Or
          </p>
          <Button
            onClick={onButtonSynchronyClick}
            className=" whitespace-normal text-[14px] font-semibold text-white bg-gray20 h-[76px] flex gap-[17px] items-center rounded-[8px] w-full"
          >
            <Image
              src={SynchronyImg}
              alt="SynchronyImg"
              className="max-w-[76px]"
            />
            Checkout with Synchrony
          </Button> */}
        </div>
      </div>
    </>
  );
};

export default OrderSummary;
